import { cn } from '@commenda/utils';
import {
  Bar,
  CartesianGrid,
  Cell,
  BarChart as RechartsBarChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { ChartColorHelper } from './chartColors';
import {
  AXIS_TICK,
  BOUNDARY_LABEL_PROPS,
  BOUNDARY_LINE_CLASS,
  ChartHelper,
  DEFAULT_VALUE_FORMATTER,
  GRID_CLASS,
  PRELIMINARY_BAR_OPACITY,
} from './chartHelpers';
import type { IBaseChartProps } from './chartTypes';

export interface IBarChartProps extends IBaseChartProps {
  type?: 'default' | 'stacked';
}

export const BarChart = ({
  data,
  index,
  categories,
  colors,
  valueFormatter = DEFAULT_VALUE_FORMATTER,
  type = 'default',
  showLegend = true,
  showGridLines = true,
  showXAxis = true,
  sparseXLabels = false,
  showYAxis = true,
  yAxisWidth = 56,
  autoMinValue = false,
  minValue,
  maxValue,
  customTooltip,
  className,
}: IBarChartProps) => {
  const categoryColors = ChartColorHelper.categoryColors(categories, colors);
  const stackId = type === 'stacked' ? 'stack' : undefined;
  const Tip = customTooltip ?? ChartTooltip;
  const lastClosed = ChartHelper.boundaryLabel(data, index);

  return (
    <div className={cn('flex h-80 w-full flex-col', className)}>
      {showLegend ? (
        <ChartLegend
          className="mb-sm"
          items={categories
            .filter((c) => categoryColors.get(c) !== 'transparent')
            .map((c) => ({ name: c, color: categoryColors.get(c) ?? 'gray' }))}
        />
      ) : null}
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={data}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
            stackOffset={type === 'stacked' ? 'sign' : undefined}
          >
            {showGridLines ? (
              <CartesianGrid
                className={GRID_CLASS}
                horizontal
                vertical={false}
              />
            ) : null}
            <XAxis
              dataKey={index}
              hide={!showXAxis}
              tickLine={false}
              axisLine={false}
              interval={sparseXLabels ? 0 : 'equidistantPreserveStart'}
              ticks={
                sparseXLabels ? ChartHelper.sparseTicks(data, index) : undefined
              }
              minTickGap={8}
              tick={AXIS_TICK}
              padding={{ left: 8, right: 8 }}
            />
            <YAxis
              width={yAxisWidth}
              hide={!showYAxis}
              tickLine={false}
              axisLine={false}
              domain={ChartHelper.yAxisDomain(autoMinValue, minValue, maxValue)}
              tick={AXIS_TICK}
              tickFormatter={valueFormatter}
              allowDecimals
            />
            <Tooltip
              wrapperStyle={{ outline: 'none' }}
              isAnimationActive={false}
              cursor={{ className: 'fill-bg-secondary' }}
              content={({ active, payload, label }) => (
                <Tip
                  active={active}
                  label={ChartHelper.tooltipLabel(label)}
                  payload={ChartHelper.tooltipEntries(payload, categoryColors)}
                  valueFormatter={valueFormatter}
                  preliminary={ChartHelper.tooltipPreliminary(payload)}
                />
              )}
            />
            {lastClosed !== null && (
              <ReferenceLine
                x={lastClosed}
                className={BOUNDARY_LINE_CLASS}
                stroke=""
                label={BOUNDARY_LABEL_PROPS}
              />
            )}
            {categories.map((category) => (
              <Bar
                key={category}
                name={category}
                dataKey={category}
                stackId={stackId}
                isAnimationActive={false}
                maxBarSize={40}
                className={ChartColorHelper.className(
                  categoryColors.get(category) ?? 'gray',
                  'fill'
                )}
                fill=""
              >
                {data.map((datum) => (
                  <Cell
                    key={String(datum[index])}
                    opacity={
                      ChartHelper.isPreliminary(datum)
                        ? PRELIMINARY_BAR_OPACITY
                        : 1
                    }
                  />
                ))}
              </Bar>
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
